import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  createSessionTracker,
  updateSessionTracker,
  calculateStatistics,
  getCurrentTimeOnPointe,
  getSessionSummary,
} from './statisticsCalculator';

export const useDataProcessing = (
  leftSensorValues = {},
  rightSensorValues = {},
  leftDeviceData = {},
  rightDeviceData = {},
  options = {}
) => {
  const {
    windowSize = 10,
    hotspotStdThreshold = 50,
    hotspotMeanThreshold = 50,
    enableSmoothing = true,
    smoothingFactor = 0.3,
  } = options;

  const sessionTrackerRef = useRef(createSessionTracker());

  const [leftHistory, setLeftHistory] = useState([]);
  const [rightHistory, setRightHistory] = useState([]);

  const [smoothedLeft, setSmoothedLeft] = useState({});
  const [smoothedRight, setSmoothedRight] = useState({});

  const [hotspots, setHotspots] = useState({ left: [], right: [] });

  const [statistics, setStatistics] = useState(null);

  const prevLeftRef = useRef({});
  const prevRightRef = useRef({});

  const smoothValues = useCallback((newValues, prevSmoothed, factor) => {
    const smoothed = {};

    Object.keys(newValues).forEach(key => {
      const newVal = newValues[key];
      const prevVal = prevSmoothed[key];

      if (prevVal !== undefined) {
        smoothed[key] = Math.round(factor * newVal + (1 - factor) * prevVal);
      } else {
        smoothed[key] = newVal;
      }
    });

    return smoothed;
  }, []);

  const detectHotspots = useCallback((history, stdThreshold, meanThreshold) => {
    if (history.length < 3) return [];

    const hotspots = [];

    for (let sensorIndex = 1; sensorIndex <= 64; sensorIndex++) {
      const values = history
        .map(dp => dp.values[sensorIndex])
        .filter(v => v !== undefined && v !== null);

      if (values.length < 3) continue;

      const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
      const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (values.length - 1);
      const std = Math.sqrt(variance);

      if (std >= stdThreshold && mean >= meanThreshold) {
        let magnitude = 2 + (std - 10) * (10 - 2) / (50 - 10);
        magnitude = Math.max(2, Math.min(10, magnitude));

        hotspots.push({
          index: sensorIndex,
          magnitude: Math.round(magnitude * 10) / 10,
          standardDeviation: std,
          mean: mean,
          current: values[values.length - 1]
        });
      }
    }

    return hotspots.sort((a, b) => b.magnitude - a.magnitude);
  }, []);

  useEffect(() => {
    const hasData = Object.keys(leftSensorValues).length > 0;
    const hasChanged = JSON.stringify(leftSensorValues) !== JSON.stringify(prevLeftRef.current);

    if (!hasData || !hasChanged) return;

    prevLeftRef.current = { ...leftSensorValues };

    const dataPoint = {
      timestamp: Date.now(),
      values: { ...leftSensorValues }
    };

    setLeftHistory(prev => [...prev, dataPoint].slice(-windowSize));

    if (enableSmoothing) {
      setSmoothedLeft(prev => smoothValues(leftSensorValues, prev, smoothingFactor));
    }
  }, [leftSensorValues, windowSize, enableSmoothing, smoothingFactor, smoothValues]);

  useEffect(() => {
    const hasData = Object.keys(rightSensorValues).length > 0;
    const hasChanged = JSON.stringify(rightSensorValues) !== JSON.stringify(prevRightRef.current);

    if (!hasData || !hasChanged) return;

    prevRightRef.current = { ...rightSensorValues };

    const dataPoint = {
      timestamp: Date.now(),
      values: { ...rightSensorValues }
    };

    setRightHistory(prev => [...prev, dataPoint].slice(-windowSize));

    if (enableSmoothing) {
      setSmoothedRight(prev => smoothValues(rightSensorValues, prev, smoothingFactor));
    }
  }, [rightSensorValues, windowSize, enableSmoothing, smoothingFactor, smoothValues]);

  useEffect(() => {
    const leftData = {
      onPointe: leftDeviceData.onPointe || false,
      jumpFlag: leftDeviceData.jumpFlag || false,
      turnFlag: leftDeviceData.turnFlag || false,
    };

    const rightData = {
      onPointe: rightDeviceData.onPointe || false,
      jumpFlag: rightDeviceData.jumpFlag || false,
      turnFlag: rightDeviceData.turnFlag || false,
    };

    if (leftData.jumpFlag || leftData.turnFlag || rightData.jumpFlag || rightData.turnFlag ||
        leftData.onPointe !== sessionTrackerRef.current.leftOnPointe ||
        rightData.onPointe !== sessionTrackerRef.current.rightOnPointe) {

      sessionTrackerRef.current = updateSessionTracker(
        sessionTrackerRef.current,
        leftData,
        rightData
      );
    }
  }, [
    leftDeviceData.onPointe,
    leftDeviceData.jumpFlag,
    leftDeviceData.turnFlag,
    rightDeviceData.onPointe,
    rightDeviceData.jumpFlag,
    rightDeviceData.turnFlag
  ]);

  useEffect(() => {
    const leftHotspots = detectHotspots(leftHistory, hotspotStdThreshold, hotspotMeanThreshold);
    const rightHotspots = detectHotspots(rightHistory, hotspotStdThreshold, hotspotMeanThreshold);

    setHotspots({ left: leftHotspots, right: rightHotspots });
  }, [leftHistory, rightHistory, hotspotStdThreshold, hotspotMeanThreshold, detectHotspots]);

  useEffect(() => {
    const stats = calculateStatistics(
      enableSmoothing ? smoothedLeft : leftSensorValues,
      enableSmoothing ? smoothedRight : rightSensorValues,
      sessionTrackerRef.current,
      leftDeviceData.onPointe || false,
      rightDeviceData.onPointe || false
    );

    setStatistics(stats);
  }, [
    leftSensorValues,
    rightSensorValues,
    smoothedLeft,
    smoothedRight,
    leftDeviceData.onPointe,
    rightDeviceData.onPointe,
    enableSmoothing
  ]);

  const resetSession = useCallback(() => {
    sessionTrackerRef.current = createSessionTracker();
    setLeftHistory([]);
    setRightHistory([]);
    setSmoothedLeft({});
    setSmoothedRight({});
    setHotspots({ left: [], right: [] });
    setStatistics(null);
    prevLeftRef.current = {};
    prevRightRef.current = {};
    console.log('🔄 Session reset');
  }, []);

  const getSessionData = useCallback(() => {
    return {
      ...getSessionSummary(sessionTrackerRef.current),
      finalStatistics: statistics,
      hotspots: hotspots
    };
  }, [statistics, hotspots]);

  const clearHotspotHistory = useCallback(() => {
    setLeftHistory([]);
    setRightHistory([]);
    setHotspots({ left: [], right: [] });
    console.log('🗑️ Hotspot history cleared');
  }, []);

  return useMemo(() => ({
    leftSensorValues: enableSmoothing ? smoothedLeft : leftSensorValues,
    rightSensorValues: enableSmoothing ? smoothedRight : rightSensorValues,
    rawLeftValues: leftSensorValues,
    rawRightValues: rightSensorValues,
    hotspots,
    leftHotspots: hotspots.left,
    rightHotspots: hotspots.right,
    hasHotspots: hotspots.left.length > 0 || hotspots.right.length > 0,
    statistics,
    sessionTracker: sessionTrackerRef.current,
    jumps: sessionTrackerRef.current.jumps,
    turns: sessionTrackerRef.current.turns,
    timeOnPointe: getCurrentTimeOnPointe(sessionTrackerRef.current),
    leftOnPointe: leftDeviceData.onPointe || false,
    rightOnPointe: rightDeviceData.onPointe || false,
    eitherOnPointe: (leftDeviceData.onPointe || false) || (rightDeviceData.onPointe || false),
    leftHistoryCount: leftHistory.length,
    rightHistoryCount: rightHistory.length,
    isReady: leftHistory.length >= 3 || rightHistory.length >= 3,
    resetSession,
    getSessionData,
    clearHotspotHistory,
  }), [
    enableSmoothing,
    smoothedLeft,
    smoothedRight,
    leftSensorValues,
    rightSensorValues,
    hotspots,
    statistics,
    leftDeviceData.onPointe,
    rightDeviceData.onPointe,
    leftHistory.length,
    rightHistory.length,
    resetSession,
    getSessionData,
    clearHotspotHistory
  ]);
};

export const useSingleFootProcessing = (sensorValues = {}, deviceData = {}, options = {}) => {
  const {
    windowSize = 10,
    hotspotStdThreshold = 50,
    hotspotMeanThreshold = 50,
    enableSmoothing = true,
    smoothingFactor = 0.3,
  } = options;

  const sessionTrackerRef = useRef(createSessionTracker());

  const [history, setHistory] = useState([]);
  const [smoothedValues, setSmoothedValues] = useState({});
  const [hotspots, setHotspots] = useState([]);
  const [statistics, setStatistics] = useState(null);

  const prevValuesRef = useRef(null);
  const prevDeviceDataRef = useRef({ onPointe: false });

  const sensorValuesKey = useMemo(() => {
    const keys = Object.keys(sensorValues);
    if (keys.length === 0) return '';
    return keys.map(k => `${k}:${sensorValues[k]}`).join(',');
  }, [sensorValues]);

  useEffect(() => {
    if (!sensorValuesKey || sensorValuesKey === prevValuesRef.current) {
      return;
    }

    prevValuesRef.current = sensorValuesKey;

    const dataPoint = {
      timestamp: Date.now(),
      values: { ...sensorValues }
    };

    setHistory(prev => {
      const newHistory = [...prev, dataPoint];
      return newHistory.slice(-windowSize);
    });

    if (enableSmoothing) {
      setSmoothedValues(prev => {
        const smoothed = {};
        Object.keys(sensorValues).forEach(key => {
          const newVal = sensorValues[key];
          const prevVal = prev[key];
          if (prevVal !== undefined) {
            smoothed[key] = Math.round(smoothingFactor * newVal + (1 - smoothingFactor) * prevVal);
          } else {
            smoothed[key] = newVal;
          }
        });
        return smoothed;
      });
    }
  }, [sensorValues, sensorValuesKey, windowSize, enableSmoothing, smoothingFactor]);

  useEffect(() => {
    const onPointe = deviceData.onPointe || false;
    const jumpFlag = deviceData.jumpFlag || false;
    const turnFlag = deviceData.turnFlag || false;

    const prevOnPointe = prevDeviceDataRef.current.onPointe;

    if (jumpFlag || turnFlag || onPointe !== prevOnPointe) {
      prevDeviceDataRef.current = { onPointe };

      sessionTrackerRef.current = updateSessionTracker(
        sessionTrackerRef.current,
        { onPointe, jumpFlag, turnFlag },
        null
      );
    }
  }, [deviceData.onPointe, deviceData.jumpFlag, deviceData.turnFlag]);

  useEffect(() => {
    if (history.length < 3) {
      setHotspots([]);
      return;
    }

    const detected = [];

    for (let sensorIndex = 1; sensorIndex <= 64; sensorIndex++) {
      const values = history
        .map(dp => dp.values[sensorIndex])
        .filter(v => v !== undefined && v !== null);

      if (values.length < 3) continue;

      const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
      const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (values.length - 1);
      const std = Math.sqrt(variance);

      if (std >= hotspotStdThreshold && mean >= hotspotMeanThreshold) {
        let magnitude = 2 + (std - 10) * (10 - 2) / (50 - 10);
        magnitude = Math.max(2, Math.min(10, magnitude));

        detected.push({
          index: sensorIndex,
          magnitude: Math.round(magnitude * 10) / 10,
          standardDeviation: std,
          mean: mean,
          current: values[values.length - 1]
        });
      }
    }

    setHotspots(detected.sort((a, b) => b.magnitude - a.magnitude));
  }, [history, hotspotStdThreshold, hotspotMeanThreshold]);

  useEffect(() => {
    const valuesToUse = enableSmoothing && Object.keys(smoothedValues).length > 0
      ? smoothedValues
      : sensorValues;

    const stats = calculateStatistics(
      valuesToUse,
      {},
      sessionTrackerRef.current,
      deviceData.onPointe || false,
      false
    );

    setStatistics(stats);
  }, [sensorValues, smoothedValues, deviceData.onPointe, enableSmoothing]);

  const resetSession = useCallback(() => {
    sessionTrackerRef.current = createSessionTracker();
    setHistory([]);
    setSmoothedValues({});
    setHotspots([]);
    setStatistics(null);
    prevValuesRef.current = null;
    prevDeviceDataRef.current = { onPointe: false };
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setHotspots([]);
  }, []);

  const onPointe = deviceData.onPointe || false;

  return {
    sensorValues: enableSmoothing && Object.keys(smoothedValues).length > 0 ? smoothedValues : sensorValues,
    rawValues: sensorValues,
    hotspots,
    statistics,
    onPointe,
    historyCount: history.length,
    isReady: history.length >= 3,
    jumps: sessionTrackerRef.current.jumps,
    turns: sessionTrackerRef.current.turns,
    timeOnPointe: getCurrentTimeOnPointe(sessionTrackerRef.current),
    resetSession,
    clearHistory,
  };
};
